/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import { ReactNode, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Chart } from 'react-google-charts';
import { BaseTile, BaseTileState } from './BaseTile';
import { Colors, Link, Text, Typography } from '@zapier/design-system';
import { LoadingStatus } from './Status';
import { IntegrationActivationRetention } from 'app/developer-v3/hooks/useQueryInsightsActiveUsersRetentionData';

const BACKGROUND_COLOR = Colors.PrimeWhite;

const styleChartContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  width: 100%;

  // Need to override an inline style that Google Charts applies
  [class*="google-visualization-table"] > div {
    height: 100% !important;
    max-height: 200px !important;
  }

  & table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
  }

  & th {
    word-wrap: break-word;
    white-space: pre-wrap;
    text-align: center;
    padding-top: 4px !important;
    padding-bottom: 6px !important;
  }

  & td {
    text-align: center !important;
    padding-top: 6px !important;
    padding-bottom: 8px !important;
  }

  /* table & cell borders */
  & table, th, td, tr {
    border: 1px solid #DADFE2 !important;
  }

  /* fix disappearing header border on scroll */
  & th:before {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    top: -1px;
    border-top: 1px solid #DADFE2;
  }
  & th:after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    bottom: -1px;
    border-top: 1px solid #DADFE2;
  }

  /* header row and left-most column: typography & background */
  & th, td:first-child {
    ${css(Typography.SmallPrint4)}
    background-color: ${BACKGROUND_COLOR} !important;
    background-image: none !important;
  }

  /* first column */
  & th:nth-child(1), td:nth-child(1) {
    width: 80px;
  }

  /* second column */
  & th:nth-child(2), td:nth-child(2) {
    width: 75px;
    background-color: ${BACKGROUND_COLOR} !important;
  }

  /* 3rd -> nth column (header & body)*/
  & th:nth-child(n+3), td:nth-child(n+3) {
    width: 56px;
    max-width: 56px;
  }

  /* 3rd -> nth column (body only) */
  & td:nth-child(n+3) {
    background-color:  #FAFAFA;
  }
`;

// This formatter will color table cells based on their value
const baseColorFormatter = {
  type: 'ColorFormat' as const,
  column: 2,
  ranges: [
    [0, 10, Colors.PrimeBlack, BACKGROUND_COLOR],
    [10, 20, Colors.PrimeBlack, 'rgba(14, 93, 78, 0.08)'],
    [20, 30, Colors.PrimeBlack, 'rgba(14, 93, 78, 0.18)'],
    [30, 40, Colors.PrimeBlack, 'rgba(14, 93, 78, 0.28)'],
    [40, 60, Colors.PrimeBlack, 'rgba(14, 93, 78, 0.4)'],
    [60, 80, Colors.PrimeWhite, 'rgba(14, 93, 78, 0.6)'],
    [80, 100, Colors.PrimeWhite, 'rgba(14, 93, 78, 0.8)'],
    [100, null, Colors.PrimeWhite, '#0E5D4E'],
  ],
};

// Formatters must be applied to table columns individually
const tableFormatters = [
  {
    ...baseColorFormatter,
    column: 2,
  },
  {
    ...baseColorFormatter,
    column: 3,
  },
  {
    ...baseColorFormatter,
    column: 4,
  },
  {
    ...baseColorFormatter,
    column: 5,
  },
  {
    ...baseColorFormatter,
    column: 6,
  },
  {
    ...baseColorFormatter,
    column: 7,
  },
  {
    ...baseColorFormatter,
    column: 8,
  },
  {
    ...baseColorFormatter,
    column: 9,
  },
  {
    ...baseColorFormatter,
    column: 10,
  },
  {
    ...baseColorFormatter,
    column: 11,
  },
  {
    ...baseColorFormatter,
    column: 12,
  },
  {
    ...baseColorFormatter,
    column: 13,
  },
  {
    ...baseColorFormatter,
    column: 14,
  },
];

type ActiveUsersRetentionTileProps = {
  dataTestId?: string;
  data: IntegrationActivationRetention[];
  /** e.g. Active users retention */
  headerContent: string;
  /** React node with Zinnia icon */
  icon: ReactNode;
  tooltipContent: ReactNode;
} & Omit<BaseTileState, 'isNoData'>;

function generateTableBodyCellFromValue(value) {
  const valueAsPercent = value != null ? Math.round(value * 100) : undefined;
  return {
    v: valueAsPercent,
    f: valueAsPercent != null ? `${valueAsPercent}%` : undefined,
  };
}

export default function ActiveUsersRetentionTile({
  dataTestId,
  data,
  isError,
  headerContent,
  icon,
  tooltipContent,
}: ActiveUsersRetentionTileProps) {
  const chartData = useMemo(() => {
    const headerData = [
      '',
      { type: 'number', label: 'Active\nUsers' },
      { type: 'number', label: 'Month\n0' },
      { type: 'number', label: 'Month\n1' },
      { type: 'number', label: 'Month\n2' },
      { type: 'number', label: 'Month\n3' },
      { type: 'number', label: 'Month\n4' },
      { type: 'number', label: 'Month\n5' },
      { type: 'number', label: 'Month\n6' },
      { type: 'number', label: 'Month\n7' },
      { type: 'number', label: 'Month\n8' },
      { type: 'number', label: 'Month\n9' },
      { type: 'number', label: 'Month\n10' },
      { type: 'number', label: 'Month\n11' },
      { type: 'number', label: 'Month\n12' },
    ];

    const rowData = (data || [])
      .map(d => ({
        ...d,
        first_activation_month_end_date: new Date(
          d.first_activation_month_end_date
        ),
      }))
      .sort(
        (a, b) =>
          b.first_activation_month_end_date.getTime() -
          a.first_activation_month_end_date.getTime()
      )
      .map(d => [
        format(d.first_activation_month_end_date, 'MMM yyyy'),
        d.users_active,
        generateTableBodyCellFromValue(d.users_active / d.users_active),
        generateTableBodyCellFromValue(d.month1_retention_rate),
        generateTableBodyCellFromValue(d.month2_retention_rate),
        generateTableBodyCellFromValue(d.month3_retention_rate),
        generateTableBodyCellFromValue(d.month4_retention_rate),
        generateTableBodyCellFromValue(d.month5_retention_rate),
        generateTableBodyCellFromValue(d.month6_retention_rate),
        generateTableBodyCellFromValue(d.month7_retention_rate),
        generateTableBodyCellFromValue(d.month8_retention_rate),
        generateTableBodyCellFromValue(d.month9_retention_rate),
        generateTableBodyCellFromValue(d.month10_retention_rate),
        generateTableBodyCellFromValue(d.month11_retention_rate),
        generateTableBodyCellFromValue(d.month12_retention_rate),
      ]);

    return [headerData, ...rowData];
  }, [data]);

  const [isRenderError, setIsRenderError] = useState(false);

  return (
    <BaseTile
      dataTestId={dataTestId}
      headerContent={headerContent}
      icon={icon}
      isError={isError || isRenderError}
      // use the chart loader prop to handle display of loading state
      isLoading={false}
      isNoData={!(chartData && chartData.length > 1)}
      style={{ height: 'auto', maxHeight: 410 }}
      tipContent={
        <Text type="Body1">
          You can also measure the impact of Zapier users on your own retention.{' '}
          <Link
            href="https://zapier.com/blog/measure-integration-effectiveness/"
            target="_blank"
          >
            Learn more.
          </Link>
        </Text>
      }
      tipContentStyle={{ marginTop: 20 }}
      toolTipContent={tooltipContent}
    >
      <div css={[styleChartContainer]}>
        <Chart
          chartEvents={[
            {
              // if there is an error rendering show the error state
              eventName: 'error',
              callback() {
                setIsRenderError(true);
              },
            },
          ]}
          chartType="Table"
          data={chartData}
          formatters={tableFormatters}
          height="100%"
          loader={<LoadingStatus />}
          options={{
            alternatingRowStyle: false,
            sort: 'disable',
            allowHtml: true,
          }}
          width="100%"
        />
      </div>
    </BaseTile>
  );
}
